import { Input, Directive } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { List as ImmutableList } from 'immutable';
import {
    ReportSteps,
    LegacyGender,
    ReportTypeType
} from '../constants-types/Enums';
import { List } from '../constants-types/PiList.model';
import { BehavioralReportConfiguration } from '../core-data-model/BehavioralReportConfiguration.model';
import { IDynamicallyCreatedComponent } from '../core-data-model/DynamicallyCreated.component';
import { ReportEmailConfiguration } from '../core-data-model/ReportEmailConfiguration.model';
import { SearchResult } from '../core-data-model/SearchResult.model';
import { AssessmentsApi } from '../core-services/AssessmentsApi.connect';
import { CommonErrorApi } from '../core-services/CommonErrorApi.connect';
import { ContextDataService } from '../core-services/Context.dataservice';
import { notification } from '../core-services/Notifications.service';
import { PdfsDataService } from '../core-services/Pdfs.dataservice';
import { PersonApi } from '../core-services/PersonApi.connect';
import { LanguagesApi } from '../translations/LanguagesApi/LanguagesApi.connect';

export interface AbstractReportReceiver {
    id: string;
    name: string;
    firstName?: string;
    lastName?: string;
    assessmentId: string;
    result: SearchResult;
    reportPdfPath?: BlobPart;
    reportConfig?: BehavioralReportConfiguration;
    gender?: LegacyGender;
}

@Directive()
export class AbstractReport<T extends AbstractReportReceiver>
    implements IDynamicallyCreatedComponent
{
    potentialReceivers: List<T> = ImmutableList();
    isContentLoaded = false;
    targetReport: ReportTypeType;
    submittingEmail: boolean;

    constructor(
        protected personApi: PersonApi,
        public context: ContextDataService,
        protected pdfsDataService: PdfsDataService,
        protected router: Router,
        protected route: ActivatedRoute,
        protected assessmentsApi: AssessmentsApi,
        protected commonErrorApi: CommonErrorApi,
        protected languageApi: LanguagesApi
    ) {}

    @Input()
    asBulk = false;
    @Input()
    cardClasses = '';
    get propertiesList(): string[] {
        return ['asBulk', 'cardClasses'];
    }

    steps = ReportSteps;
    step: number;

    // Required in order to jump to Preview Mode
    // or to Options mode in case of Behavioral Report
    changeOptions(jumpToStep: ReportSteps = ReportSteps.Preview) {
        this.step = jumpToStep;
    }

    goToEmail() {
        this.step = ReportSteps.Email;
    }

    isLoaded(): boolean {
        return !!this.potentialReceivers.size;
    }

    processRoute(): void {
        this.step = ReportSteps.Preview;
        this.isContentLoaded = true;
    }

    @notification(
        // TODO: create a specific key for this
        'create-job.confirmation-header',
        'EmailSentSuccessfully'
    )
    sendReportByEmails(
        emailConfig: ReportEmailConfiguration,
        peopleToReportOn: any[],
        reportConfig?: BehavioralReportConfiguration,
        language?: string
    ) {
        this.submittingEmail = true;
        return this.personApi
            .sendReportEmail(
                peopleToReportOn,
                this.targetReport,
                emailConfig,
                undefined,
                reportConfig,
                language
            )
            .then(() => {
                if (!this.asBulk) {
                    return this.router.navigate(['../overview'], {
                        relativeTo: this.route
                    });
                }
                // Resolving in case of bulk to display the Notifications
                // TODO: Need to be replaced with proper routing
                return Promise.resolve(true);
            })
            .finally(() => {
                this.submittingEmail = false;
            });
    }

    generateReport(callback: (receivers: List<T>) => Promise<BlobPart>) {
        return (
            callback(this.potentialReceivers)
                .then((url) => {
                    // TODO: this is a hack. do this is in a more declaractive way

                    this.potentialReceivers.get(0).reportPdfPath = url;
                })
                // TODO: Figure out a way to gracefully fallback on some nice samples
                // https://www.predictiveindex.com/sample-reports
                .catch((error) => {
                    console.error(error);
                    return this.commonErrorApi
                        .getErrorPdf()
                        .then((resourceUrl) => {
                            this.potentialReceivers.get(0).reportPdfPath =
                                resourceUrl;
                        });
                })
                .then(() => {
                    this.processRoute();
                })
        );
    }
}
